import React, { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { evaluateProductData } from '../services/productReadyService';
import { productDisplayNo } from '../utils/productDisplayId';
import { MAKS_TAMBAHAN, MAKS_SAIZ_FAIL, senaraiTambahan, galeriProduk, mampatFailGambar } from '../utils/gambarTambahan';
import { Btn, SuisBahasa } from '../design/ui';
import PratontonGambar from './PratontonGambar';
import PopupSahkanSemuaKad from './PopupSahkanSemuaKad';

// ═══════════════════════════════════════════════════════════════════
//  LENGKAPKAN DATA PRODUK — ditaip sendiri oleh pelanggan
//
//  Sesetengah penjual TikTok meletakkan keterangan produk di dalam GAMBAR
//  sahaja. Halaman produk itu memang tiada teks, jadi scrape takkan berjaya
//  berapa kali pun dicuba. Borang ini satu-satunya jalan untuk produk
//  tersebut boleh digunakan.
//
//  Nama medan kekal English (Description / Price / Category) — sama macam
//  borang di extension, supaya pelanggan nampak istilah yang serupa di
//  kedua-dua tempat.
// ═══════════════════════════════════════════════════════════════════

// Di luar komponen — kalau ditakrif dalam render, React kira ia komponen baharu
// setiap kali dan akan me-remount barisnya.
function CheckRow({ label, ok, hint, v39 }) {
    if (v39) {
        return (
            <li style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 15, flex: 'none', marginTop: 1, color: ok ? 'var(--s-ok)' : 'var(--s-ink-3)', ...(ok ? { fontVariationSettings: "'FILL' 1" } : null) }}
                >
                    {ok ? 'check_circle' : 'radio_button_unchecked'}
                </span>
                <span style={{ color: ok ? 'var(--s-ink-2)' : 'var(--s-ink)', fontWeight: ok ? 400 : 500 }}>
                    {label}
                    {hint && <span style={{ color: 'var(--s-ink-3)' }}> — {hint}</span>}
                </span>
            </li>
        );
    }
    return (
        <li className="flex items-start gap-1.5">
            <span
                className={`material-symbols-outlined text-[14px] shrink-0 mt-px ${ok ? 'text-emerald-500' : 'text-slate-300 dark:text-slate-600'}`}
                style={ok ? { fontVariationSettings: "'FILL' 1" } : undefined}
            >
                {ok ? 'check_circle' : 'radio_button_unchecked'}
            </span>
            <span className={ok ? 'text-slate-500 dark:text-slate-400' : 'text-slate-700 dark:text-slate-200 font-semibold'}>
                {label}
                {hint && <span className="font-normal text-slate-400"> — {hint}</span>}
            </span>
        </li>
    );
}

// `v39` = rupa v3.9. Borang ini dipakai oleh KEDUA-DUA rupa (pautan
// "Lengkapkan Data" pada kad wujud dalam rupa lama juga), jadi gaya lama
// dikekalkan sepenuhnya bila prop ini palsu — jangan gabungkan dua-dua.
export default function CompleteProductDataModal({ product, onClose, onSave, onMuatNaikGambar, v39 = false, penuh = false, t, kad = null }) {
    /* ⭐ PROP `t` — dua bahasa TANPA mengubah rupa lama. Halaman v3.9 menghantar
       fungsi terjemahannya masuk; rupa lama tidak menghantar apa-apa dan kekal
       dengan ayat Melayu di bawah, huruf demi huruf.

       Kenapa bukan useT() terus di sini: borang ini dibuka daripada KEDUA-DUA
       rupa, dan bahasa lalai app ialah English — panggilan terus bermakna
       pelanggan yang masih pada rupa LAMA tiba-tiba nampak borang English.
       Corak sama seperti DashboardSections.jsx. */
    const T = (kunci, lalai, vars) => (t ? t(kunci, vars) : lalai);

    /* ═══ MOD KAD — borang dibuka dari chip "Edit data" pada kad Workspace ═══
       (PLAN_EDIT_DATA_KAD.md, 2026-09-12). Prop `kad` = { kes, kumpulan, profil }:
       · kes 'own'      → item My Products pasangan wujud, tiada notis
       · kes 'system'   → pasangan produk System: Simpan menyalin ke My Products (notis + pilih tempat)
       · kes 'baru'     → tiada pasangan: Simpan menambah ke My Products (notis + pilih tempat)
       · kes 'tiadaLink'→ tiada link TikTok: hanya "kad ini sahaja" mungkin (suis disorok)
       Suis skop LALAI "semua" setiap kali dibuka dan TIDAK diingat (keputusan user 2026-09-12;
       dicuba lalai "kad" pada 2026-10-06 dan DITARIK BALIK hari yang sama). Pelanggan yang
       tersimpan "semua" tanpa sengaja diselamatkan oleh popup pengesahan (PopupSahkanSemuaKad),
       bukan dengan menukar lalai — lihat `mintaSimpan` di bawah.
       `kad.bilKadLain` = bilangan kad LAIN yang "semua" akan sentuh (null = tidak diketahui).
       Tanpa prop ini (halaman My Products) tiada satu pun daripada ini dirender —
       borang berkelakuan sebagaimana sebelum. Logik simpan milik pemanggil. */
    const kesKad = kad?.kes || null;
    const [skopKad, setSkopKad] = useState('semua');
    const [sahkanSemua, setSahkanSemua] = useState(false);   // popup pengesahan "semua" terbuka?
    const [profilKad, setProfilKad] = useState('');
    const [groupKad, setGroupKad] = useState('');
    const perluPilihTempat = !!kad && skopKad === 'semua' && (kesKad === 'system' || kesKad === 'baru');
    /* Group ikut profile yang dipilih — skop SAMA seperti halaman My Products
       (group berprofil hanya muncul di bawah profilnya; group tanpa profil = "Tiada profile"). */
    const groupPilihan = (kad?.kumpulan || []).filter((g) => (profilKad ? g.profileId === profilKad : !g.profileId));
    /* Notis System/Baru menjanjikan "Simpan akan menyalin/menambah ke My Products — pilih
       tempat di bawah". Itu hanya benar bila skop = 'semua'; pada "Kad ini sahaja"
       tiada apa disalin dan pemilih tempat tidak muncul, jadi notis disorok (user 2026-10-06). */
    const notisKad = !kad ? null
        : kesKad === 'system' && skopKad === 'semua' ? T('products.complete.kad.notisSystem', 'Produk ini datang dari System. Simpan akan menyalinnya ke My Products anda — pilih tempat menyimpannya di bawah.')
        : kesKad === 'baru' && skopKad === 'semua' ? T('products.complete.kad.notisBaru', 'Produk ini belum ada dalam My Products. Simpan akan menambahnya — pilih tempat menyimpannya di bawah.')
        /* Kad Shopee yang itemnya DIJUMPAI (padanan ikut rujukan Shopee, 2026-10-06): borang diisi dari
           item itu, jadi ayat "tidak dapat dipadankan" tidak benar. Simpan tetap kad ini sahaja. */
        : kesKad === 'tiadaLink' && kad.padanShopee ? T('products.complete.kad.notisShopee', 'Produk Shopee — data di bawah diambil dari My Products anda. Perubahan hanya terpakai pada kad ini.')
        : kesKad === 'tiadaLink' ? T('products.complete.kad.notisTiadaLink', 'Kad ini tiada link TikTok, jadi ia tidak dapat dipadankan dengan My Products. Perubahan hanya terpakai pada kad ini.')
        : null;
    const pilihanKad = kad
        ? { skop: kesKad === 'tiadaLink' ? 'kad' : skopKad, profileId: profilKad || null, groupId: groupKad || null }
        : undefined;

    const sd = product?.scrapedData || {};
    const [description, setDescription] = useState(sd.description || '');
    // Harga boleh sedia ada pada kad walaupun scrape tidak menemuinya.
    const [price, setPrice] = useState(sd.price || product?.price || '');
    const [category, setCategory] = useState(sd.category || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [butiranBuka, setButiranBuka] = useState(true);   // "Butiran tambahan" — terbuka secara lalai

    /* ═══ Mod PENUH — borang gabungan (arahan user 2026-08-16, Pilihan A) ═══
       Dahulu ada DUA borang berasingan: "Edit produk" (inline pada kad —
       nama/link/nombor/harga) dan borang ini (description/harga/kategori).
       HARGA wujud dalam kedua-duanya, iaitu dua tempat menulis medan yang
       sama — jemputan kepada data tidak sekata. Kini satu borang, tiga
       seksyen. Mod ini dipakai oleh rupa v3.9 SAHAJA; rupa lama kekal
       dengan dua borang asalnya. */
    const [nama, setNama] = useState(product?.name || '');
    const [gambar, setGambar] = useState(product?.imageUrl || '');

    /* ═══ Gambar daripada peranti pelanggan ═══
       Sebelum ini medan ini hanya menerima PAUTAN. Pelanggan yang gambarnya
       ada dalam telefon/komputer langsung tiada jalan — dan dalam paparan
       senarai (paparan lalai halaman ini) borang inilah satu-satunya laluan.

       ⛔ Fail DITAHAN sehingga Simpan, BUKAN dimuat naik sebaik dipilih.
       Borang ini ada butang Batal: muat naik awal bermakna setiap kali
       pelanggan berubah fikiran, satu fail yatim tinggal dalam simpanan dan
       memakan kuota 2GB dia selama-lamanya — tiada apa pun yang membersihnya.
       Jangan tukar kepada muat naik serta-merta tanpa membina pembersihan. */
    const MAKS_GAMBAR = 5 * 1024 * 1024;   // 5 MB
    const [gambarFail, setGambarFail] = useState(null);
    const [gambarPratonton, setGambarPratonton] = useState('');
    const [memuatNaik, setMemuatNaik] = useState(false);
    const gambarInputRef = useRef(null);

    /* Object URL pratonton WAJIB dilepaskan — tanpa ini ia memegang fail
       dalam memori sepanjang hayat tab. Pembersihan effect memulangkan URL
       LAMA setiap kali nilai bertukar, jadi ia meliputi tukar-fail dan
       tutup-borang sekali gus. */
    useEffect(() => () => { if (gambarPratonton) URL.revokeObjectURL(gambarPratonton); }, [gambarPratonton]);

    const handlePilihGambar = (e) => {
        const fail = e.target.files?.[0];
        e.target.value = '';   // benarkan pilih fail yang SAMA sekali lagi
        if (!fail) return;
        if (!/^image\//.test(fail.type || '')) {
            setError(T('products.complete.imgNotImage', 'Fail itu bukan gambar. Sila pilih fail JPG atau PNG.'));
            return;
        }
        if (fail.size > MAKS_GAMBAR) {
            setError(T('products.complete.imgTooBig', 'Gambar terlalu besar — maksimum 5 MB.'));
            return;
        }
        setError('');
        setGambarPratonton(URL.createObjectURL(fail));
        setGambarFail(fail);
    };

    const buangGambarPilihan = () => {
        setGambarPratonton('');
        setGambarFail(null);
    };

    /* ═══ GAMBAR TAMBAHAN untuk Flow (2026-09-11) — lihat utils/gambarTambahan.js ═══
       Satu senarai, dua sumber: pautan galeri scrape TikTok (serta-merta, tiada
       muat naik) dan fail peranti (DITAHAN sehingga Simpan — sebab yang sama
       seperti gambar utama di atas: Batal tidak boleh meninggalkan fail yatim).
       Hanya dalam mod PENUH (rupa v3.9) dan hanya bila pelanggan memang boleh
       menulis gambar produk ini (onMuatNaikGambar ada). */
    const bolehTambahan = penuh && !!onMuatNaikGambar;
    const galeri = useMemo(() => galeriProduk(product), [product]);
    const [tambahan, setTambahan] = useState(() => senaraiTambahan(product).map((url) => ({ id: url, url, fail: null, pratonton: '' })));
    const [bukaGaleri, setBukaGaleri] = useState(false);
    const tambahanInputRef = useRef(null);
    const tambahanAsal = useMemo(() => senaraiTambahan(product), [product]);
    // Object URL setiap fail peranti dilepaskan bila borang ditutup.
    useEffect(() => () => { tambahan.forEach((g) => { if (g.pratonton) URL.revokeObjectURL(g.pratonton); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const tambahanPenuh = tambahan.length >= MAKS_TAMBAHAN;
    const togolGaleri = (url) => {
        setError('');
        setTambahan((prev) => {
            const ada = prev.find((g) => g.url === url);
            if (ada) return prev.filter((g) => g.url !== url);
            if (prev.length >= MAKS_TAMBAHAN) { setError(T('products.complete.extraFull', 'Had dicapai — buang satu gambar dahulu.')); return prev; }
            return [...prev, { id: url, url, fail: null, pratonton: '' }];
        });
    };
    const pilihFailTambahan = async (e) => {
        const fails = Array.from(e.target.files || []);
        e.target.value = '';
        if (!fails.length) return;
        setError('');
        const ruang = MAKS_TAMBAHAN - tambahan.length;
        if (ruang <= 0) { setError(T('products.complete.extraFull', 'Had dicapai — buang satu gambar dahulu.')); return; }
        const diterima = [];
        for (const f of fails.slice(0, ruang)) {
            if (!/^image\//.test(f.type || '')) { setError(T('products.complete.imgNotImage', 'Fail itu bukan gambar. Sila pilih fail JPG atau PNG.')); continue; }
            if (f.size > MAKS_SAIZ_FAIL) { setError(T('products.complete.imgTooBig', 'Gambar terlalu besar — maksimum 5 MB.')); continue; }
            const kecil = await mampatFailGambar(f);
            diterima.push({ id: 'fail_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7), url: '', fail: kecil, pratonton: URL.createObjectURL(kecil) });
        }
        if (fails.length > ruang) setError(T('products.complete.extraFull', 'Had dicapai — buang satu gambar dahulu.'));
        if (diterima.length) setTambahan((prev) => [...prev, ...diterima].slice(0, MAKS_TAMBAHAN));
    };
    const buangTambahan = (id) => {
        setError('');
        setTambahan((prev) => {
            const g = prev.find((x) => x.id === id);
            if (g && g.pratonton) URL.revokeObjectURL(g.pratonton);
            return prev.filter((x) => x.id !== id);
        });
    };

    /* ═══ PAPARAN GAMBAR BESAR (2026-09-13) — tekan gambar, keluar saiz besar (PratontonGambar.jsx) ═══
       Soalan user: pelanggan tiada cara melihat gambar utama / tambahan / galeri dalam saiz besar.
       `pratonton` = { jenis: 'produk' | 'galeri', mula }. Senarai dikira SETIAP render, bukan disalin
       masa dibuka — butang Pilih/Buang dalam galeri mesti terus menunjukkan keadaan terkini. */
    const [pratonton, setPratonton] = useState(null);
    const gambarUtamaPapar = gambarPratonton || gambar;
    const senaraiPratontonProduk = [
        ...(gambarUtamaPapar ? [{ src: gambarUtamaPapar, tanda: T('gbrBesar.utama', 'Utama') }] : []),
        ...(bolehTambahan ? tambahan.map((g) => ({ src: g.pratonton || g.url })) : []),
    ];
    // Gambar tambahan ke-i berada di kedudukan i+1 dalam pemapar (gambar utama di hadapan).
    const indeksTambahanPertama = gambarUtamaPapar ? 1 : 0;
    const senaraiPratontonGaleri = galeri.map((u) => ({
        src: u,
        tanda: tambahan.some((g) => g.url === u) ? T('gbrBesar.dipilih', 'Dipilih') : '',
    }));
    const aksiGaleri = (idx, it) => {
        const dipilih = tambahan.some((g) => g.url === it.src);
        const sekat = !dipilih && tambahanPenuh;
        return {
            label: dipilih ? T('gbrBesar.buang', 'Buang pilihan') : T('gbrBesar.pilih', 'Pilih gambar ini'),
            ikon: dipilih ? 'remove' : 'add',
            utama: !dipilih,
            disabled: saving || sekat,
            nota: sekat
                ? T('gbrBesar.penuh', `Had ${MAKS_TAMBAHAN} gambar tambahan sudah penuh — buang satu dahulu.`, { max: MAKS_TAMBAHAN })
                : T('gbrBesar.kiraTambahan', `Gambar tambahan ${tambahan.length}/${MAKS_TAMBAHAN}`, { n: tambahan.length, max: MAKS_TAMBAHAN }),
            onClick: () => togolGaleri(it.src),
        };
    };

    const [pautan, setPautan] = useState(product?.link || '');
    /* ★ Shopee affiliate link (v4 FB Reels) — BERASINGAN daripada `link` (TikTok).
       Pelanggan tampal manual buat masa ini. Toggle Run FB Reels masih locked
       admin, jadi field ini cuma menyimpan data untuk kelak — tiada apa berjalan. */
    const [shopeeLink, setShopeeLink] = useState(product?.shopeeLink || '');
    const [nombor, setNombor] = useState(product?.number ?? '');
    const [hargaAsal, setHargaAsal] = useState(product?.originalPrice ?? '');
    const [diskaun, setDiskaun] = useState(product?.discount ?? '');

    /* ═══ Medan BONUS — semuanya menyuap AI, semuanya kini boleh diubah ═══
       Sebelum ini medan ini hanya BOLEH DILIHAT (modal Scraped Data): kalau
       scrape menangkap spesifikasi yang salah, pelanggan nampak tetapi tidak
       boleh membetulkannya. Senarai (variasi/kelebihan/spesifikasi/ulasan)
       disunting sebagai teks satu-baris-satu — bentuk yang sama seperti borang
       di extension, supaya pelanggan tidak belajar dua cara berbeza. */
    const brs = (v) => (Array.isArray(v) ? v : []);
    const [jenama, setJenama] = useState(sd.brand || '');
    const [rating, setRating] = useState(sd.rating ?? '');
    const [ratingCount, setRatingCount] = useState(sd.ratingCount ?? '');
    const [terjual, setTerjual] = useState(sd.soldCount ?? '');
    const [kedai, setKedai] = useState(sd.shopName || '');
    const [variasi, setVariasi] = useState(
        brs(sd.variants).map((v) => (typeof v === 'string' ? v : v?.name || '')).filter(Boolean).join('\n'),
    );
    const [kelebihan, setKelebihan] = useState(
        (Array.isArray(sd.sellingPoints) ? sd.sellingPoints : String(sd.sellingPoints || '').split('\n')).filter(Boolean).join('\n'),
    );
    /* ⚠️ Spesifikasi datang dalam DUA bentuk: senarai [{name,value}] (scrape baharu)
       ATAU objek {Nama: Nilai} (scrape lama, cth item 2026-07). Dulu hanya senarai
       diprapapar — objek keluar KOSONG, dan Simpan menulis senarai kosong = spesifikasi
       lama HILANG senyap (ditangkap 2026-09-12 semasa ujian Edit data dari kad).
       Kini kedua-dua bentuk dibaca; laluan tulis kekal senarai (buildDataProdukBlock
       memahami kedua-duanya). */
    const senaraiSpek = (v) => (Array.isArray(v)
        ? v
        : (v && typeof v === 'object' ? Object.entries(v).map(([name, value]) => ({ name, value })) : []));
    const [spesifikasi, setSpesifikasi] = useState(
        senaraiSpek(sd.specifications).map((s) => `${s?.name || s?.key || ''} : ${s?.value ?? ''}`).filter((x) => x.trim() !== ':').join('\n'),
    );
    const [ulasan, setUlasan] = useState(
        brs(sd.topReviews).map((r) => String(r?.text || '').replace(/\s+/g, ' ').trim()).filter(Boolean).join('\n'),
    );

    /* Tukar teks satu-baris-satu kembali kepada bentuk yang dibaca
       `buildDataProdukBlock`. Bentuk mesti SAMA seperti hasil scrape —
       kalau tidak, medan yang disunting pengguna diam-diam hilang daripada
       prompt walaupun ia kelihatan tersimpan. */
    const kepadaSenarai = (teks) => String(teks || '').split('\n').map((b) => b.trim()).filter(Boolean);
    const kepadaSpek = (teks) => kepadaSenarai(teks).map((baris) => {
        const i = baris.indexOf(':');
        return i === -1
            ? { name: baris, value: '' }
            : { name: baris.slice(0, i).trim(), value: baris.slice(i + 1).trim() };
    });

    // Produk yang datanya SUDAH lengkap (READY) — borang ini kini terbuka untuk
    // semua produk, bukan yang PENDING sahaja, jadi ayatnya perlu berubah:
    // "Lengkapkan" (kad kosong) lawan "Edit" (betulkan data sedia ada).
    const sudahAdaData = !!product?.hasIntelligence;

    /* ★ 2026-10-03 — DARI MANA data produk ini di-scrape (aduan user: pelanggan keliru sama ada
       data datang dari link TikTok atau Shopee). Dibaca dari `scrapedData.source`; scrape lama
       yang tiada medan itu dikenal melalui alamat sumbernya. null = tiada rekod (cth produk
       yang diisi tangan) → tanda sumber tidak dipapar, bukan diteka. */
    const sumberData = (() => {
        const jenis = String(sd.source || '').toLowerCase();
        const alamat = String(sd.sourceUrl || sd.resolvedUrl || '');
        if (jenis === 'shopee' || /shopee\.|shope\.ee|shp\.ee/i.test(alamat)) return 'shopee';
        if (jenis === 'tiktok' || /tiktok\.com/i.test(alamat)) return 'tiktok';
        return null;
    })();

    // Senarai semak HIDUP — guna peraturan yang SAMA dengan penilaian sebenar,
    // supaya mustahil borang kata "cukup" tetapi kad tersimpan sebagai PENDING.
    /* Mod penuh menilai medan identiti sekali (nama/gambar/link) kerana ia
       kini sebahagian daripada syarat. Mod lama hanya menilai `sd` — perangai
       rupa lama tidak berubah. */
    /* Fail yang MENUNGGU muat naik dikira sebagai gambar yang sudah ada —
       kalau tidak, senarai semak berkata "Product Image belum ada" sedangkan
       pelanggan baru sahaja memilih gambarnya, dan ayat status di bawahnya
       memberi amaran turun ke PENDING tanpa sebab. */
    const gambarUntukSemakan = gambarFail ? 'pending-upload' : gambar;
    const check = useMemo(
        () => (penuh
            /* ⚠️ KEMBAR (2026-10-06): `simpanDariKad` (utils/editDataProduk.js) menilai SEMULA dengan
               medan rujukan yang sama (link · shopeeLink · shopeeProductUrl) sebelum menulis perenggan
               data kad. Tambah medan rujukan di sini → tambah di sana, kalau tidak borang berkata READY
               tetapi perenggan tidak ditulis, senyap. */
            /* ★ 2026-10-03 — "Product link" = link TikTok ATAU rujukan Shopee (link affiliate yang
               sedang ditaip, atau `shopeeProductUrl` item import Shopee). Medan yang SAMA dibaca
               laluan simpan (`saveManualProductData` menilai {...item, ...idPatch}) — dahulu borang
               hanya menghantar link TikTok, lalu produk Shopee diberi amaran PENDING palsu. */
            ? evaluateProductData({ description, price, category }, { name: nama, imageUrl: gambarUntukSemakan, link: pautan, shopeeLink, shopeeProductUrl: product?.shopeeProductUrl })
            : evaluateProductData({ description, price, category })),
        [description, price, category, penuh, nama, gambarUntukSemakan, pautan, shopeeLink, product?.shopeeProductUrl],
    );

    const missingSet = new Set(check.missing.map(m => m.split(' ')[0]));

    /* `skopPaksa` ('semua' | 'kad') — pilihan yang dibuat DALAM popup pengesahan; mengatasi suis
       skop untuk simpanan ini. ⛔ Butang memanggil `handleSave()` TANPA hujah: `onClick={handleSave}`
       akan menghantar objek event sebagai `skopPaksa`. */
    const handleSave = async (skopPaksa) => {
        const pilihanSimpan = (pilihanKad && (skopPaksa === 'semua' || skopPaksa === 'kad'))
            ? { ...pilihanKad, skop: skopPaksa }
            : pilihanKad;
        setSaving(true);
        setError('');
        try {
            /* Gambar daripada peranti dimuat naik DI SINI — satu tekan Simpan,
               satu komit. Kalau muat naik gagal, borang berhenti di sini dan
               TIADA apa pun tertulis; pelanggan boleh cuba semula tanpa
               kehilangan apa yang dia sudah taip. */
            let urlGambar = gambar;
            if (gambarFail && onMuatNaikGambar) {
                setMemuatNaik(true);
                urlGambar = await onMuatNaikGambar(gambarFail);
                setMemuatNaik(false);
            }
            /* Gambar TAMBAHAN dari peranti dimuat naik di sini juga — satu-satu, dalam
               susunan pelanggan pilih. Satu gagal = borang berhenti (pelanggan nampak
               sebab, tiada apa tertulis), sama peraturan seperti gambar utama. */
            let extraImageUrls;
            let extraDibuang;
            if (bolehTambahan) {
                setMemuatNaik(true);
                const akhir = [];
                for (const g of tambahan) {
                    if (g.url) { akhir.push(g.url); continue; }
                    if (g.fail) akhir.push(await onMuatNaikGambar(g.fail));
                }
                setMemuatNaik(false);
                extraImageUrls = akhir.slice(0, MAKS_TAMBAHAN);
                extraDibuang = tambahanAsal.filter((u) => !extraImageUrls.includes(u));
            }
            /* Mod penuh menghantar medan identiti sekali — satu Simpan
               menulis kedua-duanya, jadi mustahil harga tersimpan berbeza
               antara dua borang. */
            await onSave(penuh
                ? {
                    description, price, category,
                    name: nama, imageUrl: urlGambar, link: pautan, shopeeLink, number: nombor,
                    originalPrice: hargaAsal, discount: diskaun,
                    extraImageUrls, extraDibuang,
                    kad: pilihanSimpan,
                    bonus: {
                        brand: jenama.trim(),
                        rating: String(rating ?? '').trim(),
                        ratingCount: String(ratingCount ?? '').trim(),
                        soldCount: String(terjual ?? '').trim(),
                        shopName: kedai.trim(),
                        variants: kepadaSenarai(variasi).map((n) => ({ name: n })),
                        sellingPoints: kepadaSenarai(kelebihan),
                        specifications: kepadaSpek(spesifikasi),
                        topReviews: kepadaSenarai(ulasan).map((teks) => ({ text: teks })),
                    },
                }
                : { description, price, category });
        } catch (err) {
            setMemuatNaik(false);
            setError(err?.message || T('products.complete.saveFailed', 'Gagal menyimpan. Sila cuba sekali lagi.'));
            setSaving(false);
        }
    };

    /* ★ 2026-10-06 — PINTU SIMPAN mod kad (v3.9). "Semua kad + My Products" ialah lalai, jadi
       pelanggan yang hanya mahu membetulkan SATU kad boleh menulis ke My Products + semua kad
       produk itu tanpa sedar. Bila skop = 'semua' DAN ada kad lain yang akan terkena (atau
       bilangannya tidak diketahui), tanya dahulu — SEBELUM sebarang muat naik gambar, supaya
       Batal tidak meninggalkan fail yatim. Tiada kad lain = tiada popup (tiada apa mengejutkan). */
    const perluSahkanSemua = !!kad && v39 && kesKad !== 'tiadaLink' && skopKad === 'semua'
        && (kad.bilKadLain == null || kad.bilKadLain > 0);
    const mintaSimpan = () => {
        if (saving) return;
        if (perluSahkanSemua) { setError(''); setSahkanSemua(true); return; }
        handleSave();
    };

    /* ★ 2026-10-03 — SENARAI SEMAK READY sebagai satu blok supaya ia boleh diletak di ATAS
       sekali dalam mod penuh (arahan user: "syarat READY paling penting — tanpa READY produk
       tidak boleh run di Workspace") dan kekal di BAWAH dalam rupa lama. Isi blok TIDAK diubah. */
    /* Pil "data di-scrape dari …" (mod penuh v3.9). Latar kad + garis supaya ia kekal
       kelihatan di dalam kotak syarat, yang latarnya bertukar hijau lembut bila READY. */
    const namaPlatform = { tiktok: 'TikTok', shopee: 'Shopee' };
    const pilSumber = (teks, warna = 'var(--s-ok)') => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, lineHeight: 1, padding: '4px 9px', borderRadius: 999, background: 'var(--s-card)', border: `1px solid ${warna}`, color: warna, whiteSpace: 'nowrap' }}>
            <span className="material-symbols-outlined" aria-hidden style={{ fontSize: 13 }}>database</span>
            {teks}
        </span>
    );

    const senaraiSemak = (
        <div
            className={v39 ? undefined : `rounded-xl p-3 border ${check.ready
                ? 'bg-emerald-50/60 dark:bg-emerald-900/15 border-emerald-200/60 dark:border-emerald-800/40'
                : 'bg-slate-50 dark:bg-slate-800/40 border-slate-200/70 dark:border-slate-700/50'}`}
            style={v39 ? {
                padding: 12, borderRadius: 'var(--s-r)',
                /* ★ 2026-10-03 (arahan user) — belum cukup = warna tema status PENDING (kuning), bukan kelabu. */
                background: check.ready ? 'var(--s-ok-soft)' : 'var(--s-warn-soft, var(--s-fill))',
                border: `1px solid ${check.ready ? 'var(--s-ok)' : 'var(--s-warn)'}`,
            } : undefined}
        >
            {/* ★ 2026-10-03 (arahan user) — sumber scrape duduk DI SINI supaya dua soalan dijawab di
                satu tempat: data ini di-scrape dari mana, dan syarat apa yang belum cukup. */}
            {penuh && sumberData && (
                <div style={{ marginBottom: 9 }}>
                    {pilSumber(T('products.complete.sumber.pill', `Data produk di-scrape dari ${namaPlatform[sumberData]}`, { platform: namaPlatform[sumberData] }), check.ready ? 'var(--s-ok)' : 'var(--s-warn)')}
                </div>
            )}
            <ul
                className={v39 ? undefined : 'space-y-1 text-[11px] m-0 p-0 list-none'}
                style={v39 ? { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5 } : undefined}
            >
                {/* Senarai semak menunjukkan apa yang WAJIB sahaja.
                    Harga & kategori tidak lagi wajib — ia bonus. */}
                {penuh ? (
                    <>
                        <CheckRow v39={v39} label={T('products.complete.lbl.nama', 'Product Name')} ok={!missingSet.has('Product')} />
                        <CheckRow v39={v39} label={T('products.complete.lbl.gambar', 'Product Image')} ok={!!String(gambarUntukSemakan || '').trim()} />
                        <CheckRow v39={v39} label={T('products.complete.lbl.idProduk', 'Product ID (TikTok link)')} ok={!!String(pautan || '').trim()} />
                        <CheckRow v39={v39} label={T('products.complete.lbl.desc', 'Description')} ok={!missingSet.has('Description')} />
                    </>
                ) : (
                    <>
                        <CheckRow v39={v39} label={T('products.complete.lbl.desc', 'Description')} ok={!missingSet.has('Description')} />
                        <CheckRow v39={v39} label={T('products.complete.lbl.harga', 'Price')} ok={!missingSet.has('Price')} />
                        <CheckRow v39={v39} label={T('products.complete.lbl.kategori', 'Category')} ok={!missingSet.has('Category')} />
                    </>
                )}
            </ul>
            <p
                className={v39 ? undefined : `text-[11px] font-bold mt-2 mb-0 ${check.ready ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}
                style={v39 ? { fontSize: 12.5, fontWeight: 500, margin: '9px 0 0', color: check.ready ? 'var(--s-ok)' : 'var(--s-warn)' } : undefined}
            >
                {/* Ayat berbeza untuk kad yang MEMANG sudah READY — memberitahu
                    pelanggan bahawa ia "akan bertukar READY" sedangkan ia sudah
                    READY hanya mengelirukan. */}
                {check.ready
                    ? (sudahAdaData
                        ? T('products.complete.status.readyKeep', 'Cukup — produk ini kekal READY selepas disimpan.')
                        : T('products.complete.status.readyBecome', 'Cukup — produk ini akan bertukar READY selepas disimpan.'))
                    : (sudahAdaData
                        ? T('products.complete.status.downgrade', '⚠️ Belum cukup — produk ini akan turun semula ke PENDING selepas disimpan.')
                        : T('products.complete.status.stayPending', 'Belum cukup — produk masih kekal PENDING selepas disimpan.'))}
            </p>
        </div>
    );

    /* ★ 2026-10-03 — KOTAK "PRODUCT LINKS" (lakaran diluluskan user: Idea B dalam satu kotak
       berlatar kontra). Label platform berwarna melekat di depan setiap medan link:
       TikTok = dakwat tema, Shopee = oren jenama (warna SAMA dengan tanda platform dalam
       senarai — PlatformImportV39.jsx). Medan + state TIDAK berubah (`pautan`, `shopeeLink`). */
    const awalanLink = (label, latar, dakwat) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', minWidth: 78, padding: '0 12px', fontSize: 12, fontWeight: 700, background: latar, color: dakwat, borderRadius: '8px 0 0 8px' }}>
            {label}
        </span>
    );
    const kotakLink = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 12, borderRadius: 12, background: 'var(--s-fill)', border: '1px solid var(--s-line-strong, var(--s-line))' }}>
            <div style={{ display: 'flex' }}>
                {awalanLink('TikTok', 'var(--s-ink)', 'var(--s-card)')}
                <input className="s-input" style={{ flex: 1, minWidth: 0, borderRadius: '0 8px 8px 0', background: 'var(--s-card)' }} aria-label={T('products.complete.lbl.tiktok', 'TikTok Link')} value={pautan} onChange={(e) => setPautan(e.target.value)} placeholder="https://shop.tiktok.com/view/product/…" />
            </div>
            <div style={{ display: 'flex' }}>
                {awalanLink('Shopee', '#EE4D2D', '#fff')}
                <input className="s-input" style={{ flex: 1, minWidth: 0, borderRadius: '0 8px 8px 0', background: 'var(--s-card)' }} aria-label={T('products.complete.lbl.shopee', 'Shopee Affiliate Link')} value={shopeeLink} onChange={(e) => setShopeeLink(e.target.value)} placeholder="https://s.shopee.com.my/…" />
            </div>
            <p className="s-hint" style={{ margin: 0 }}>
                {T('products.complete.linksHint', 'Satu link sudah memadai. Link TikTok untuk beg kuning TikTok, link Shopee untuk Facebook Reels.')}
            </p>
        </div>
    );

    /* ★ 2026-10-03 — JALUR STATUS (mod penuh): status READY/PENDING + sumber scrape + empat
       syarat SEBARIS. Menggantikan senarai semak menegak dalam mod penuh; `senaraiSemak`
       kekal untuk rupa lama. Tanda setiap syarat dibaca dari `check.missing` dengan padanan
       TEPAT (dahulu padanan perkataan pertama "Product" menandakan Product Name gagal bila
       yang hilang sebenarnya Product ID). */
    const syaratOk = {
        nama: !check.missing.includes('Product Name'),
        gambar: !check.missing.includes('Product Image'),
        link: !check.missing.includes('Product ID'),
        desc: !check.missing.some((m) => m.startsWith('Description')),
    };
    const warnaStatus = check.ready ? 'var(--s-ok)' : 'var(--s-warn)';
    const cipSyarat = (ok, label) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, whiteSpace: 'nowrap', color: ok ? 'var(--s-ink-2)' : 'var(--s-ink)', fontWeight: ok ? 400 : 600 }}>
            <span className="material-symbols-outlined" aria-hidden style={{ fontSize: 15, color: ok ? 'var(--s-ok)' : 'var(--s-warn)', ...(ok ? { fontVariationSettings: "'FILL' 1" } : null) }}>
                {ok ? 'check_circle' : 'radio_button_unchecked'}
            </span>
            {label}
        </span>
    );
    const jalurStatus = (
        <div style={{ padding: 12, borderRadius: 'var(--s-r)', background: check.ready ? 'var(--s-ok-soft)' : 'var(--s-warn-soft, var(--s-fill))', border: `1px solid ${warnaStatus}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                <span style={{ flex: 1, minWidth: 180, fontSize: 13, fontWeight: 600, color: warnaStatus }}>
                    {check.ready
                        ? T('products.complete.status.ready', 'Cukup — produk ini READY dan boleh run di Workspace.')
                        : T('products.complete.status.notReady', 'Belum cukup — produk ini PENDING dan belum boleh run di Workspace.')}
                </span>
                {sumberData && pilSumber(T('products.complete.sumber.pill', `Data produk di-scrape dari ${namaPlatform[sumberData]}`, { platform: namaPlatform[sumberData] }), warnaStatus)}
            </div>
            <div style={{ display: 'flex', gap: '6px 16px', flexWrap: 'wrap' }}>
                {cipSyarat(syaratOk.nama, T('products.complete.chk.nama', 'Name'))}
                {cipSyarat(syaratOk.gambar, T('products.complete.chk.gambar', 'Image'))}
                {cipSyarat(syaratOk.link, T('products.complete.chk.link', 'Product link'))}
                {cipSyarat(syaratOk.desc, T('products.complete.lbl.desc', 'Description'))}
            </div>
        </div>
    );

    /* Tajuk seksyen bernombor (mod penuh). */
    const tajukSeksyen = (no, teks) => (
        <p style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: 'var(--s-ink-3)', margin: 0, textTransform: 'uppercase' }}>
            <span style={{ display: 'inline-grid', placeItems: 'center', width: 18, height: 18, borderRadius: 9, background: 'var(--s-fill)', border: '1px solid var(--s-line)', fontSize: 10.5, letterSpacing: 0, color: 'var(--s-ink-2)' }}>{no}</span>
            {teks}
        </p>
    );

    /* "Butiran tambahan" — lapan medan pilihan. TERBUKA secara lalai (keputusan user 3 Okt);
       kiraan menunjukkan berapa yang sudah berisi. Rating + Rating Count dikira satu. */
    const adaIsi = (v) => String(v ?? '').trim() !== '';
    const butiranIsi = [jenama, kedai, adaIsi(rating) || adaIsi(ratingCount) ? '1' : '', terjual, variasi, kelebihan, spesifikasi, ulasan].filter(adaIsi).length;

    return createPortal(
        /* ⚠️ `s-root` WAJIB pada lapisan luar: modal ini di-portal ke
           `document.body`, iaitu DI LUAR pokok .s-root — tanpa kelas ini
           pemilih `.s-root .s-modal` tidak sepadan dan panel keluar tanpa
           gaya. (Token warna sendiri selamat kerana ia turut diikat pada
           `body.s-v39`.) */
        <div className={v39
            ? 's-root s-modal'
            : 'fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4'}>
            <div
                className={v39
                    ? 's-modal__panel'
                    : 'w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800'}
                style={v39 ? { maxWidth: 560 } : undefined}
            >

                {/* Header */}
                <div className={v39 ? 's-modal__head' : 'flex items-start gap-3 p-5 border-b border-slate-100 dark:border-slate-800'}>
                    {v39 ? (
                        <span aria-hidden style={{
                            width: 34, height: 34, flex: 'none', borderRadius: 10,
                            background: 'var(--s-fill)', border: '1px solid var(--s-line)',
                            display: 'grid', placeItems: 'center', color: 'var(--s-ink-2)',
                        }}>
                            <span className="material-symbols-outlined" style={{ fontSize: 17 }}>edit_note</span>
                        </span>
                    ) : (
                        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-[18px] text-primary">edit_note</span>
                        </div>
                    )}
                    <div className="flex-1 min-w-0">
                        <h3
                            className={v39 ? undefined : 'text-sm font-extrabold text-slate-800 dark:text-slate-100 m-0'}
                            style={v39 ? { fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', margin: 0 } : undefined}
                        >
                            {sudahAdaData
                                ? T('products.complete.title.edit', 'Edit Data Produk')
                                : T('products.complete.title.fill', 'Lengkapkan Data Produk')}
                        </h3>
                        <p
                            className={v39 ? undefined : 'text-[11px] text-slate-400 m-0 mt-0.5 truncate'}
                            style={v39 ? { fontSize: 12, color: 'var(--s-ink-3)', margin: '2px 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : undefined}
                        >
                            #{productDisplayNo(product)} · {product?.name}
                        </p>
                    </div>
                    {/* Pill bahasa EN/BM dalam borang (arahan user 2026-09-13) — tetapan SAMA seperti
                        suis pada kepala halaman (satu bahasa seluruh app). v3.9 sahaja. */}
                    {v39 && <SuisBahasa />}
                    <button
                        onClick={onClose}
                        disabled={saving}
                        aria-label={T('products.complete.closeAria', 'Tutup')}
                        className={v39 ? 's-modal__x' : 'p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40'}
                    >
                        <span className="material-symbols-outlined" style={{ fontSize: v39 ? 16 : 18 }}>close</span>
                    </button>
                </div>

                <div
                    className={v39 ? undefined : 'p-5 space-y-4'}
                    style={v39 ? { padding: 16, display: 'flex', flexDirection: 'column', gap: 14, overflowY: 'auto' } : undefined}
                >
                    {/* ═══ MOD KAD — skop + tempat simpan (v3.9), DI ATAS SEKALI (arahan user 2026-09-12: ia keputusan
                        paling penting dalam borang). Disorok untuk kad tanpa link. ═══ */}
                    {kad && v39 && kesKad !== 'tiadaLink' && (
                        /* Permukaan TERBALIK (arahan user 2026-09-13: kontras ikut tema semasa — terang = gelap,
                           gelap = terang). Corak sama kad Extension + bar pukal; tiada warna baharu.
                           Kawalan = DUA kad pilihan (Idea B), ganti suis segmen yang "nampak cacat". */
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 12, borderRadius: 12, background: 'var(--s-primary)', color: 'var(--s-primary-ink)' }}>
                            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', color: 'var(--s-primary-ink-2)', margin: 0, textTransform: 'uppercase' }}>
                                {T('products.complete.kad.skopTitle', 'Guna perubahan pada')}
                            </p>
                            <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                {[
                                    { v: 'semua', ikon: 'sync_alt', tajuk: T('products.complete.kad.skopSemua', 'Semua kad + My Products'), nota: T('products.complete.kad.skopHintSemua', 'Item My Products dikemas kini, dan semua kad produk ini di Workspace ikut berubah.') },
                                    { v: 'kad', ikon: 'crop_portrait', tajuk: T('products.complete.kad.skopKad', 'Kad ini sahaja'), nota: T('products.complete.kad.skopHintKad', 'Hanya kad ini berubah. My Products dan kad lain tidak disentuh.') },
                                ].map((o) => {
                                    const on = skopKad === o.v;
                                    return (
                                        <button
                                            key={o.v}
                                            type="button"
                                            role="radio"
                                            aria-checked={on}
                                            onClick={() => setSkopKad(o.v)}
                                            style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, textAlign: 'left',
                                                padding: '10px 11px', borderRadius: 10, cursor: 'pointer', font: 'inherit',
                                                background: on ? 'var(--s-primary-fill)' : 'transparent',
                                                border: on ? '1.5px solid var(--s-primary-ink)' : '1px solid var(--s-primary-line)',
                                                color: 'var(--s-primary-ink)',
                                            }}
                                        >
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
                                                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>{on ? 'radio_button_checked' : 'radio_button_unchecked'}</span>
                                                <span style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25 }}>{o.tajuk}</span>
                                            </span>
                                            <span style={{ fontSize: 11.5, lineHeight: 1.4, color: 'var(--s-primary-ink-2)', paddingLeft: 22 }}>{o.nota}</span>
                                        </button>
                                    );
                                })}
                            </div>
                            {perluPilihTempat && (
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                        <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--s-primary-ink)' }}>{T('products.complete.kad.profil', 'Profile')}</span>
                                        <select className="s-input" value={profilKad} onChange={(e) => { setProfilKad(e.target.value); setGroupKad(''); }}>
                                            <option value="">{T('products.complete.kad.tiadaProfil', 'Tiada profile')}</option>
                                            {(kad.profil || []).map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                                        </select>
                                    </label>
                                    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                        <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--s-primary-ink)' }}>{T('products.complete.kad.group', 'Group')}</span>
                                        <select className="s-input" value={groupKad} onChange={(e) => setGroupKad(e.target.value)}>
                                            <option value="">{T('products.complete.kad.tiadaGroup', 'Tiada group')}</option>
                                            {groupPilihan.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                                        </select>
                                    </label>
                                </div>
                            )}
                        </div>
                    )}

                    {penuh ? (
                        <>
                            {/* ═══ MOD PENUH — susunan baharu (lakaran diluluskan user 3 Okt 2026) ═══
                                jalur status → 1 Product → 2 Links and price → 3 Script data.
                                Semua medan + state SAMA seperti dahulu; hanya susunan dan rupa berubah. */}
                            {jalurStatus}

                            {/* Notis mod kad — hanya bila borang dibuka dari kad Workspace (v3.9). */}
                            {kad && v39 && notisKad && (
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, background: 'var(--s-fill)', border: '1px solid var(--s-line)', fontSize: 12.5, color: 'var(--s-ink-2)', lineHeight: 1.45 }}>
                                    <span className="material-symbols-outlined" aria-hidden style={{ fontSize: 16, color: 'var(--s-ink-3)', flex: 'none', marginTop: 1 }}>info</span>
                                    <span>{notisKad}</span>
                                </div>
                            )}

                            {/* ── 1 · PRODUCT ── */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                {tajukSeksyen(1, T('products.complete.sek.produk', 'Product'))}
                                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                    <div style={{ width: 84, flex: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                                        {gambarUtamaPapar
                                            ? (
                                                /* Tekan = paparan besar (gambar utama + tambahan, gambar 1). */
                                                <button
                                                    type="button"
                                                    className="pgb-petak"
                                                    onClick={() => setPratonton({ jenis: 'produk', mula: 0 })}
                                                    title={T('gbrBesar.lihat', 'Lihat saiz besar')}
                                                    aria-label={T('gbrBesar.lihat', 'Lihat saiz besar')}
                                                >
                                                    <img src={gambarUtamaPapar} alt="" style={{ width: 84, height: 84, display: 'block', objectFit: 'cover', borderRadius: 10, border: '1px solid var(--s-line)' }} />
                                                    <span className="pgb-petak__lapis" aria-hidden>
                                                        <span className="material-symbols-outlined">zoom_in</span>
                                                    </span>
                                                </button>
                                            )
                                            : <span style={{ width: 84, height: 84, borderRadius: 10, background: 'var(--s-fill)', border: '1px dashed var(--s-line-strong, var(--s-line))' }} />}
                                        {onMuatNaikGambar && (
                                            <Btn variant="ghost" size="sm" onClick={() => gambarInputRef.current?.click()} disabled={saving} style={{ width: 84, justifyContent: 'center' }}>
                                                {T('products.manual.upload', 'Upload')}
                                            </Btn>
                                        )}
                                        {onMuatNaikGambar && (
                                            <input ref={gambarInputRef} type="file" accept="image/*" onChange={handlePilihGambar} style={{ display: 'none' }} />
                                        )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <div>
                                            <label className="s-lbl">{T('products.complete.lbl.nama', 'Product Name')}</label>
                                            <input className="s-input" value={nama} onChange={(e) => setNama(e.target.value)} placeholder={T('products.complete.lbl.namaPh', 'Product name')} />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 168px', gap: 10 }}>
                                            <div>
                                                <label className="s-lbl">{T('products.complete.lbl.kategori', 'Category')}</label>
                                                <input className="s-input" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Beauty / Health / Home…" />
                                            </div>
                                            <div>
                                                <label className="s-lbl">{T('products.complete.lbl.nombor', 'Number')}</label>
                                                <input className="s-input" value={nombor} onChange={(e) => setNombor(e.target.value)} placeholder="001" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="s-lbl">{T('products.complete.lbl.linkGambar', 'Image link')}</label>
                                    {/* Medan pautan DIMATIKAN semasa ada fail menunggu: dua sumber gambar pada satu
                                        masa bermakna pelanggan tidak tahu yang mana akan menang. Fail yang menang. */}
                                    <input
                                        className="s-input"
                                        value={gambarFail ? '' : gambar}
                                        onChange={(e) => setGambar(e.target.value)}
                                        placeholder={gambarFail ? gambarFail.name : 'https://…'}
                                        disabled={!!gambarFail || saving}
                                    />
                                    {gambarFail && (
                                        <p style={{ fontSize: 12.5, color: 'var(--s-ink-3)', margin: '6px 0 0' }}>
                                            <span style={{ color: 'var(--s-ink-2)' }}>{gambarFail.name}</span>
                                            {' — '}
                                            {T('products.complete.imgPending', 'akan dimuat naik semasa Simpan')}
                                            {' · '}
                                            <button
                                                type="button"
                                                onClick={buangGambarPilihan}
                                                disabled={saving}
                                                style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: 'var(--s-ink-2)', textDecoration: 'underline', cursor: 'pointer' }}
                                            >
                                                {T('products.complete.imgRemove', 'Buang')}
                                            </button>
                                        </p>
                                    )}
                                </div>
                                {bolehTambahan && (
                                    <div>
                                        <label className="s-lbl" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                            <span>{T('products.complete.extraLabel', 'Gambar tambahan untuk Flow')}</span>
                                            <span style={{ fontSize: 11, color: 'var(--s-ink-3)', fontWeight: 400 }}>{tambahan.length}/{MAKS_TAMBAHAN}</span>
                                        </label>
                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                            {tambahan.map((g, i) => (
                                                <div key={g.id} style={{ position: 'relative', width: 44, height: 44, flex: 'none' }}>
                                                    {/* Tekan = paparan besar pada kedudukan gambar ini. ✕ di bawah ialah butang
                                                        BERASINGAN (bukan anak butang ini) — buang tidak membuka pemapar. */}
                                                    <button
                                                        type="button"
                                                        className="pgb-petak"
                                                        onClick={() => setPratonton({ jenis: 'produk', mula: indeksTambahanPertama + i })}
                                                        title={g.fail ? `${g.fail.name} · ${T('gbrBesar.lihat', 'Lihat saiz besar')}` : T('gbrBesar.lihat', 'Lihat saiz besar')}
                                                        aria-label={T('gbrBesar.lihatNo', `Lihat gambar ${i + 2} saiz besar`, { n: i + 2 })}
                                                    >
                                                        <img src={g.pratonton || g.url} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--s-line)', display: 'block' }} />
                                                        <span className="pgb-petak__lapis" aria-hidden>
                                                            <span className="material-symbols-outlined">zoom_in</span>
                                                        </span>
                                                    </button>
                                                    <span style={{ position: 'absolute', left: 2, top: 2, minWidth: 14, height: 14, padding: '0 3px', borderRadius: 5, background: 'rgba(0,0,0,.62)', color: '#fff', fontSize: 9, fontWeight: 600, lineHeight: '14px', textAlign: 'center', pointerEvents: 'none' }}>{i + 2}</span>
                                                    <button type="button" onClick={() => buangTambahan(g.id)} disabled={saving} aria-label={T('products.complete.imgRemove', 'Buang')}
                                                        style={{ position: 'absolute', right: -5, top: -5, width: 16, height: 16, borderRadius: 8, border: '1px solid var(--s-line-strong)', background: 'var(--s-card)', color: 'var(--s-ink)', fontSize: 10, lineHeight: '14px', padding: 0, cursor: 'pointer' }}>×</button>
                                                </div>
                                            ))}
                                            {galeri.length > 0 && (
                                                <Btn variant="ghost" size="sm" onClick={() => setBukaGaleri((v) => !v)} disabled={saving} style={{ height: 36, flex: 'none' }}>
                                                    {sumberData === 'shopee'
                                                        ? T('products.complete.extraGalleryShopee', `Galeri Shopee (${galeri.length})`, { n: galeri.length })
                                                        : T('products.complete.extraGallery', `Galeri TikTok (${galeri.length})`, { n: galeri.length })}
                                                </Btn>
                                            )}
                                            <Btn variant="ghost" size="sm" onClick={() => tambahanInputRef.current?.click()} disabled={saving || tambahanPenuh} style={{ height: 36, flex: 'none' }}>
                                                {T('products.complete.extraUpload', 'Upload')}
                                            </Btn>
                                            <input ref={tambahanInputRef} type="file" accept="image/*" multiple onChange={pilihFailTambahan} style={{ display: 'none' }} />
                                        </div>
                                        {bukaGaleri && galeri.length > 0 && (
                                            <div style={{ marginTop: 8, padding: 8, border: '1px solid var(--s-line)', borderRadius: 10, background: 'var(--s-fill)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                                                    <span style={{ fontSize: 12, color: 'var(--s-ink-3)' }}>{T('products.complete.extraPickHint', 'Tekan gambar untuk tambah atau buang. Tekan ikon kanta untuk lihat saiz besar.')}</span>
                                                    <button type="button" onClick={() => setBukaGaleri(false)} style={{ background: 'none', border: 0, padding: 0, font: 'inherit', fontSize: 12, color: 'var(--s-ink-2)', textDecoration: 'underline', cursor: 'pointer' }}>{T('products.complete.extraDone', 'Selesai')}</button>
                                                </div>
                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 56px)', gap: 6 }}>
                                                    {galeri.map((u, gi) => {
                                                        const dipilih = tambahan.some((g) => g.url === u);
                                                        return (
                                                            /* Tekan gambar = pilih/buang (kekal). Kanta di bucu = paparan besar
                                                               dengan butang Pilih/Buang. Dua butang ADIK-BERADIK, bukan bersarang
                                                               (butang dalam butang = HTML tidak sah). */
                                                            <div key={u} className="pgb-galeri" style={{ width: 56, height: 56 }}>
                                                                <button type="button" onClick={() => togolGaleri(u)} disabled={saving} title={u}
                                                                    style={{ position: 'relative', width: 56, height: 56, padding: 0, borderRadius: 8, overflow: 'hidden', cursor: 'pointer', border: dipilih ? '2px solid var(--s-ink)' : '1px solid var(--s-line)', opacity: (!dipilih && tambahanPenuh) ? 0.45 : 1, background: 'var(--s-card)' }}>
                                                                    <img src={u} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                                                    {dipilih && <span style={{ position: 'absolute', right: 3, top: 3, width: 16, height: 16, borderRadius: 8, background: 'var(--s-ink)', color: 'var(--s-card)', fontSize: 11, lineHeight: '16px', textAlign: 'center' }}>✓</span>}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className="pgb-galeri__kanta"
                                                                    onClick={() => setPratonton({ jenis: 'galeri', mula: gi })}
                                                                    title={T('gbrBesar.lihat', 'Lihat saiz besar')}
                                                                    aria-label={T('gbrBesar.lihat', 'Lihat saiz besar')}
                                                                >
                                                                    <span className="material-symbols-outlined">zoom_in</span>
                                                                </button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                        <p className="s-hint" style={{ margin: '6px 0 0' }}>
                                            {tambahan.some((g) => g.fail)
                                                ? T('products.complete.extraPending', 'Fail yang dipilih akan dimuat naik semasa Simpan.')
                                                : T('products.complete.extraHintUmum', `Sehingga ${MAKS_TAMBAHAN} gambar tambahan. Flow memakai kesemuanya sebagai rujukan supaya produk keluar tepat. Pilih dari galeri produk (tanpa muat naik) atau muat naik dari peranti anda.`, { max: MAKS_TAMBAHAN })}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* ── 2 · LINKS AND PRICE ── */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 14, borderTop: '1px solid var(--s-line)' }}>
                                {tajukSeksyen(2, T('products.complete.sek.linkHarga', 'Links and price'))}
                                {kotakLink}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                    <div>
                                        <label className="s-lbl">{T('products.complete.lbl.harga', 'Price')}</label>
                                        <input className="s-input" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="RM29.90" />
                                    </div>
                                    <div>
                                        <label className="s-lbl">{T('products.complete.lbl.hargaAsal', 'Original Price')}</label>
                                        <input className="s-input" value={hargaAsal} onChange={(e) => setHargaAsal(e.target.value)} placeholder="RM59.00" />
                                    </div>
                                    <div>
                                        <label className="s-lbl">{T('products.complete.lbl.diskaun', 'Discount')}</label>
                                        <input className="s-input" value={diskaun} onChange={(e) => setDiskaun(e.target.value)} placeholder="-50%" />
                                    </div>
                                </div>
                                <p className="s-hint" style={{ margin: 0 }}>
                                    {T('products.complete.priceNote', 'Harga ini dipakai pada kad DAN dalam skrip video — satu tempat sahaja, jadi ia tidak boleh jadi berbeza.')}
                                </p>
                            </div>

                            {/* ── 3 · SCRIPT DATA ── */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 14, borderTop: '1px solid var(--s-line)' }}>
                                {tajukSeksyen(3, T('products.complete.lbl.skrip', 'Script Data'))}
                                <div>
                                    <label className="s-lbl">{T('products.complete.lbl.desc', 'Description')}</label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        rows={6}
                                        placeholder={T('products.complete.descPlaceholder', 'Taip atau salin keterangan produk di sini — ciri-ciri, kelebihan, bahan, saiz, cara guna…')}
                                        className="s-input"
                                        style={{ height: 'auto', minHeight: 130, padding: '9px 11px', lineHeight: 1.5, resize: 'vertical' }}
                                    />
                                    <p className="s-hint">
                                        {T('products.complete.descHint', 'Tiada had panjang — lagi banyak butiran yang anda beri, lagi baik skrip video yang dihasilkan.')}
                                    </p>
                                </div>

                                {/* Butiran tambahan — lapan medan pilihan, TERBUKA secara lalai. */}
                                <button
                                    type="button"
                                    aria-expanded={butiranBuka}
                                    onClick={() => setButiranBuka((v) => !v)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--s-line)', background: 'var(--s-card)', color: 'var(--s-ink)', font: 'inherit', cursor: 'pointer', textAlign: 'left' }}
                                >
                                    <span className="material-symbols-outlined" aria-hidden style={{ fontSize: 18, color: 'var(--s-ink-2)' }}>{butiranBuka ? 'expand_more' : 'chevron_right'}</span>
                                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{T('products.complete.butiran.tajuk', 'Butiran tambahan untuk skrip yang lebih tajam')}</span>
                                    <span style={{ fontSize: 12, color: 'var(--s-ink-3)' }}>{T('products.complete.butiran.kira', `${butiranIsi} daripada 8 diisi`, { n: butiranIsi, total: 8 })}</span>
                                </button>
                                {butiranBuka && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                            <div>
                                                <label className="s-lbl">{T('products.complete.lbl.jenama', 'Brand')}</label>
                                                <input className="s-input" value={jenama} onChange={(e) => setJenama(e.target.value)} placeholder="—" />
                                            </div>
                                            <div>
                                                <label className="s-lbl">{T('products.complete.lbl.kedai', 'Shop Name')}</label>
                                                <input className="s-input" value={kedai} onChange={(e) => setKedai(e.target.value)} placeholder="—" />
                                            </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                            <div>
                                                <label className="s-lbl">{T('products.complete.lbl.rating', 'Rating')}</label>
                                                <input className="s-input" value={rating} onChange={(e) => setRating(e.target.value)} placeholder="4.9" />
                                            </div>
                                            <div>
                                                <label className="s-lbl">{T('products.complete.lbl.ratingCount', 'Rating Count')}</label>
                                                <input className="s-input" value={ratingCount} onChange={(e) => setRatingCount(e.target.value)} placeholder="9992" />
                                            </div>
                                            <div>
                                                <label className="s-lbl">{T('products.complete.lbl.terjual', 'Sold Count')}</label>
                                                <input className="s-input" value={terjual} onChange={(e) => setTerjual(e.target.value)} placeholder="68682" />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="s-lbl">{T('products.complete.lbl.variasi', 'Variants')}</label>
                                            <textarea
                                                className="s-input"
                                                style={{ height: 'auto', minHeight: 70, padding: '9px 11px', lineHeight: 1.5, resize: 'vertical' }}
                                                value={variasi}
                                                onChange={(e) => setVariasi(e.target.value)}
                                                placeholder={'Satu variasi satu baris\n30ml x 1\n30ml x 2'}
                                            />
                                        </div>

                                        <div>
                                            <label className="s-lbl">{T('products.complete.lbl.kelebihan', 'Selling Points')}</label>
                                            <textarea
                                                className="s-input"
                                                style={{ height: 'auto', minHeight: 70, padding: '9px 11px', lineHeight: 1.5, resize: 'vertical' }}
                                                value={kelebihan}
                                                onChange={(e) => setKelebihan(e.target.value)}
                                                placeholder={'Satu kelebihan satu baris\nCepat meresap\nTiada bau'}
                                            />
                                        </div>

                                        <div>
                                            <label className="s-lbl">{T('products.complete.lbl.spek', 'Specifications')}</label>
                                            <textarea
                                                className="s-input"
                                                style={{ height: 'auto', minHeight: 80, padding: '9px 11px', lineHeight: 1.5, resize: 'vertical' }}
                                                value={spesifikasi}
                                                onChange={(e) => setSpesifikasi(e.target.value)}
                                                placeholder={'Satu baris satu spesifikasi, guna tanda titik bertindih\nBahan : Plastik\nWarna : Putih'}
                                            />
                                        </div>

                                        <div>
                                            <label className="s-lbl">{T('products.complete.lbl.ulasan', 'Customer Reviews')}</label>
                                            <textarea
                                                className="s-input"
                                                style={{ height: 'auto', minHeight: 90, padding: '9px 11px', lineHeight: 1.5, resize: 'vertical' }}
                                                value={ulasan}
                                                onChange={(e) => setUlasan(e.target.value)}
                                                placeholder={'Satu ulasan satu baris — ayat sebenar pelanggan paling berguna untuk hook'}
                                            />
                                            <p className="s-hint" style={{ margin: 0 }}>
                                                {T('products.complete.bonusNote', 'Medan di atas tidak wajib. Setiap satu yang diisi dihantar kepada AI dan menajamkan skrip video anda.')}
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            {/* ═══ RUPA LAMA / MOD RINGKAS — kepingan asal, tidak diubah ═══ */}
                            <p
                                className={v39 ? 's-hint' : 'text-[11px] leading-relaxed text-slate-500 dark:text-slate-400 m-0'}
                                style={v39 ? { margin: 0 } : undefined}
                            >
                                {/* ⚠️ Sandaran rupa lama dikekalkan sebagai JSX kerana perkataan
                                    "seterusnya" dibalut <strong> di situ; kamus menyimpan ayat
                                    biasa, jadi dalam v3.9 ayat itu keluar tanpa huruf tebal. */}
                                {sudahAdaData ? T('products.complete.intro.edit', (
                                    <>
                                        Maklumat di bawah ialah bahan yang digunakan untuk menghasilkan skrip video
                                        produk ini. Anda boleh membetulkan atau menambah butiran bila-bila masa —
                                        perubahan hanya digunakan pada generate yang <strong>seterusnya</strong>,
                                        skrip yang sudah dihasilkan tidak berubah.
                                    </>
                                )) : T('products.complete.intro.fill', (
                                    <>
                                        Sesetengah penjual meletakkan keterangan produk di dalam gambar sahaja, jadi
                                        maklumat itu tidak dapat diambil secara automatik. Sila taip semula maklumat
                                        tersebut di sini. Selepas disimpan, produk ini terus boleh digunakan.
                                    </>
                                ))}
                            </p>

                            {/* Notis mod kad — hanya bila borang dibuka dari kad Workspace (v3.9). */}
                            {kad && v39 && notisKad && (
                                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, background: 'var(--s-fill)', border: '1px solid var(--s-line)', fontSize: 12.5, color: 'var(--s-ink-2)', lineHeight: 1.45 }}>
                                    <span className="material-symbols-outlined" aria-hidden style={{ fontSize: 16, color: 'var(--s-ink-3)', flex: 'none', marginTop: 1 }}>info</span>
                                    <span>{notisKad}</span>
                                </div>
                            )}

                            {/* Description */}
                            <div>
                                <label className={v39 ? 's-lbl' : 'block text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1'}>
                                    {T('products.complete.lbl.desc', 'Description')} <span style={v39 ? { color: 'var(--s-stop)' } : undefined} className={v39 ? undefined : 'text-rose-500'}>*</span>
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={8}
                                    placeholder={T('products.complete.descPlaceholder', 'Taip atau salin keterangan produk di sini — ciri-ciri, kelebihan, bahan, saiz, cara guna…')}
                                    className={v39 ? 's-input' : 'w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-[12px] leading-relaxed text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-y'}
                                    /* `.s-input` ditetapkan tinggi 36px untuk medan satu baris —
                                       kotak teks perlu tinggi automatik, jadi ia ditindih di sini. */
                                    style={v39 ? { height: 'auto', minHeight: 160, padding: '9px 11px', lineHeight: 1.5, resize: 'vertical' } : undefined}
                                />
                                <p className={v39 ? 's-hint' : 'text-[10px] mt-1 m-0 text-slate-400'}>
                                    {T('products.complete.descHint', 'Tiada had panjang — lagi banyak butiran yang anda beri, lagi baik skrip video yang dihasilkan.')}
                                </p>
                            </div>

                            {/* Price + Category — dalam mod PENUH, Harga sudah berada dalam
                                seksyen Harga di atas, jadi hanya Kategori ditunjukkan di sini.
                                Dua medan harga dalam satu borang = pengguna isi satu, tertanya
                                yang mana diguna. */}
                            <div
                                className={v39 ? undefined : 'grid grid-cols-2 gap-3'}
                                style={v39 ? { display: 'grid', gridTemplateColumns: penuh ? '1fr' : '1fr 1fr', gap: 12 } : undefined}
                            >
                                <div style={penuh ? { display: 'none' } : undefined}>
                                    <label className={v39 ? 's-lbl' : 'block text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1'}>
                                        {T('products.complete.lbl.harga', 'Price')} <span style={v39 ? { color: 'var(--s-stop)' } : undefined} className={v39 ? undefined : 'text-rose-500'}>*</span>
                                    </label>
                                    <input
                                        value={price}
                                        onChange={(e) => setPrice(e.target.value)}
                                        placeholder="RM29.90"
                                        className={v39 ? 's-input' : 'w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-[12px] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'}
                                    />
                                </div>
                                <div>
                                    <label className={v39 ? 's-lbl' : 'block text-[11px] font-bold text-slate-600 dark:text-slate-300 mb-1'}>
                                        {T('products.complete.lbl.kategori', 'Category')} {!penuh && <span style={v39 ? { color: 'var(--s-stop)' } : undefined} className={v39 ? undefined : 'text-rose-500'}>*</span>}
                                    </label>
                                    <input
                                        value={category}
                                        onChange={(e) => setCategory(e.target.value)}
                                        placeholder="Beauty / Health / Home…"
                                        className={v39 ? 's-input' : 'w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-[12px] text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary'}
                                    />
                                </div>
                            </div>

                            {senaraiSemak}
                        </>
                    )}

                    {error && (
                        <p
                            className={v39 ? undefined : 'text-[11px] text-rose-600 dark:text-rose-400 m-0'}
                            style={v39 ? { fontSize: 12.5, color: 'var(--s-stop)', margin: 0 } : undefined}
                        >{error}</p>
                    )}
                </div>

                {/* Footer */}
                <div
                    className={v39 ? undefined : 'flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-100 dark:border-slate-800'}
                    style={v39 ? { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--s-line)', flex: 'none' } : undefined}
                >
                    {v39 ? (
                        <>
                            {penuh && (
                                <span style={{ flex: 1, minWidth: 0, fontSize: 12, color: 'var(--s-ink-3)' }}>
                                    {T('products.complete.kakiNota', 'Perubahan dipakai pada generate yang seterusnya.')}
                                </span>
                            )}
                            <Btn variant="ghost" size="sm" onClick={onClose} disabled={saving}>{T('common.cancel', 'Batal')}</Btn>
                            <Btn variant="primary" size="sm" onClick={mintaSimpan} disabled={saving}>
                                {/* Muat naik gambar boleh ambil beberapa saat pada talian
                                    perlahan — label yang kekal "Menyimpan…" membuat borang
                                    nampak tergantung. */}
                                {memuatNaik
                                    ? T('products.card.uploading', 'Uploading...')
                                    : saving ? T('common.saving', 'Menyimpan…') : T('common.save', 'Simpan')}
                            </Btn>
                        </>
                    ) : (
                        <>
                            <button
                                onClick={onClose}
                                disabled={saving}
                                className="px-4 py-2 rounded-xl text-[12px] font-bold text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40"
                            >
                                {T('common.cancel', 'Batal')}
                            </button>
                            <button
                                onClick={() => handleSave()}
                                disabled={saving}
                                className="px-5 py-2 rounded-xl text-[12px] font-extrabold text-white bg-primary hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5"
                            >
                                {saving && <span className="material-symbols-outlined text-[14px] animate-spin">progress_activity</span>}
                                {saving ? T('common.saving', 'Menyimpan…') : T('common.save', 'Simpan')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Paparan gambar besar — di-portal sendiri ke <body> (z-index di atas borang ini). */}
            {pratonton && (
                <PratontonGambar
                    senarai={pratonton.jenis === 'galeri' ? senaraiPratontonGaleri : senaraiPratontonProduk}
                    mula={pratonton.mula}
                    tajuk={pratonton.jenis === 'galeri' ? (sumberData === 'shopee' ? T('gbrBesar.galeriShopee', 'Galeri Shopee') : T('gbrBesar.galeri', 'Galeri TikTok')) : nama}
                    aksi={pratonton.jenis === 'galeri' ? aksiGaleri : null}
                    onTutup={() => setPratonton(null)}
                    T={T}
                />
            )}

            {/* Popup pengesahan "Semua kad + My Products" — di-portal sendiri ke <body>, di atas borang. */}
            <PopupSahkanSemuaKad
                buka={sahkanSemua && !saving}
                bilKadLain={kad?.bilKadLain ?? null}
                itemBaru={kesKad === 'system' || kesKad === 'baru'}
                onSemua={() => { setSahkanSemua(false); handleSave('semua'); }}
                onKadSahaja={() => { setSahkanSemua(false); setSkopKad('kad'); handleSave('kad'); }}
                onBatal={() => setSahkanSemua(false)}
                T={T}
            />
        </div>,
        document.body,
    );
}
